import { useTranslation } from 'react-i18next';
import { Card, BodyText, Box, MaterialSymbol, withStyles } from 'vopay';
import { Button } from '#shared/components/UI';
import Dialog from '#shared/components/Dialog/Dialog';
import styles from './BlockingDialog.module.scss';
import blockSubAccountIcon from './icons/blockSubAccount.svg';

const cn = withStyles(styles);

export type BlockAccountDialogProps = {
  accountName: string;
  onConfirm: (val: true) => void;
  onCancel: () => void;
};

// The per-account block confirm, opened via useDialog().open() from the list's row action (ID-13193).
// Ported from client-dashboard's SubAccountsList/BlockingDialog; rewired to shared's Dialog + UI Button.
export default function BlockAccountDialog({ accountName, onConfirm, onCancel }: BlockAccountDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList.BlockAccountDialog' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });

  return (
    <Card className={cn('dialogueCard')}>
      <div className={cn('icon')}>
        <img src={blockSubAccountIcon} alt={t('title', { defaultValue: 'Block Sub-Account' })} width="80" height="80" />
      </div>

      <div className={cn('title')}>
        <Dialog.Title level="3">{t('title', { defaultValue: 'Block Sub-Account' })}</Dialog.Title>
      </div>

      <BodyText className={cn('bodyText')}>
        <div className={cn('description')}>{t('description', { defaultValue: 'Blocking a sub-account will:' })}</div>

        <ul className={cn('bulletList')}>
          <li>{t('stopOutbound', { defaultValue: 'Stop all outbound transactions from the sub-account.' })}</li>
          <li>{t('redirectInbound', { defaultValue: 'Redirect all inbound transactions to the parent account.' })}</li>
        </ul>
      </BodyText>

      <BodyText className={cn('bodyText', 'description')}>
        {t('unblockMessage', { defaultValue: 'You can unblock anytime to restore normal behaviour.' })}
      </BodyText>

      <Box className={cn('infoBox')}>
        <MaterialSymbol icon="lan" fillAmount={1} size="20px" color="#2F82FFBF" className={cn('infoIcon')} />

        <BodyText className={cn('bodyText')} size="rg">
          {t('impactMessage', { accountName, defaultValue: 'This will impact {{accountName}} only.' })}
        </BodyText>
      </Box>

      <div className={cn('buttonContainer')}>
        <Button onClick={onCancel}>{tButtons('cancel', { defaultValue: 'Cancel' })}</Button>

        <Button colorScheme="primary" onClick={() => onConfirm(true)}>
          {t('confirmButton', { defaultValue: 'Confirm & Block' })}
        </Button>
      </div>
    </Card>
  );
}
